import { CurriculumDisicplineTypes } from "utils/constants";

const courseOfSemester = semester => Math.ceil(semester / 2);

const unique = values => [...new Set(values)];

const semesterKey = semester => `semester-${semester}`;
const courseKey = course => `course-${course}`;

// Which groups a discipline shows up in. A discipline planned by semester belongs to that semester plus
// any semester where it already has attestations. A discipline planned by course only - Medicine has
// semesters up to the 3rd course and bare courses 4-6 after that - carries no semester of its own and
// gets a single group for its course; no semester is ever fabricated from the course number.
const groupKeysOfDiscipline = discipline => {
  const attestationSemesters = unique((discipline?.attestations ?? []).map(({ semester }) => semester).filter(Boolean));

  if (discipline?.semester) return unique([discipline.semester, ...attestationSemesters]).map(semesterKey);

  // Orphan discipline (no longer in the curriculum) with nothing recorded - there is nothing to manage.
  if (discipline?.type === CurriculumDisicplineTypes.None && !attestationSemesters.length) return [];

  return discipline?.course ? [courseKey(discipline.course)] : [];
};

const newGroup = (key, discipline) => {
  const semester = key.startsWith("semester-") ? Number(key.slice("semester-".length)) : null;
  return {
    key,
    semester,
    course: semester ? courseOfSemester(semester) : discipline.course,
    disciplines: [],
  };
};

// A course group sorts after the semester groups of the same course.
const orderInCourse = group => group.semester ?? Number.MAX_SAFE_INTEGER;

// Splits the flat discipline list into groups, keeping the order the backend sends (course, semester,
// disciplineId) within each group. A semester group shows only the attestations recorded in that
// semester; a course group holds every attestation of the discipline, whatever semester it carries.
export const groupDisciplines = disciplines => {
  const groups = new Map();

  (disciplines ?? []).forEach(discipline => {
    groupKeysOfDiscipline(discipline).forEach(key => {
      if (!groups.has(key)) groups.set(key, newGroup(key, discipline));
      const group = groups.get(key);
      group.disciplines.push({
        ...discipline,
        attestations: group.semester
          ? (discipline.attestations ?? []).filter(attestation => attestation.semester === group.semester)
          : (discipline.attestations ?? []),
      });
    });
  });

  return [...groups.values()].sort((a, b) => a.course - b.course || orderInCourse(a) - orderInCourse(b));
};

export const groupLabel = group =>
  group.semester ? `Семестър ${group.semester} (курс ${group.course})` : `${group.course} курс`;

// The save endpoint takes a semester in the URL. A course group has none of its own, so it uses the
// second semester of its course - that is how the backend records these attestations: 8, 10 and 12
// for the 4th, 5th and 6th course.
export const semesterOfGroup = group => group.semester ?? group.course * 2;
